import React, { useState, useEffect, useRef } from "react";

import {
  Button,
  Card,
  CardHeader,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from "@mui/icons-material";

/*
  Props:
  - inventoryPools: { id, inventoryPool }[]
  - contracts: { id, totalTons, tonsSold, pricePerTon, supplierId, projectId,
      projectDrawDownOrder, Supplier, Project, allocations: [{ inventoryPoolId, tonsAllocated }] }[]
  - suppliers: { id, supplier }[]
  - projects: { id, projectKey, projectName }[]
  - onUpdateDrawdownOrder(poolId, [{ contractId, order }])
  - onNavigateToTab(tab)

  Pool inventory is computed from contracts: for each pool, find allocations pointing to that pool.
  tonsSold on the contract is the total across all pools; we attribute it proportionally by
  tonsAllocated share of that pool's allocation.
*/
export function InventoryPoolsSection({
  inventoryPools,
  contracts,
  suppliers,
  projects,
  onAddInventoryPool,
  onUpdateDrawdownOrder,
  onNavigateToTab
}) {
  const [expandedPools, setExpandedPools] = useState(new Set());
  const initializedPools = useRef(false);
  useEffect(() => {
    if (!initializedPools.current && inventoryPools.length > 0) {
      initializedPools.current = true;
      setExpandedPools(new Set(inventoryPools.map((p) => p.id)));
    }
  }, [inventoryPools]);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newPoolName, setNewPoolName] = useState("");

  // localOrders[poolId] = [{ contractId, order }] — tracks in-progress swaps
  // before the query re-fetches.
  const [localOrders, setLocalOrders] = useState({});
  useEffect(() => {
    setLocalOrders({});
  }, [contracts]);

  const handleCreatePool = () => {
    if (!newPoolName) { return; }
    onAddInventoryPool({ inventoryPool: newPoolName });
    setNewPoolName("");
    setIsCreateOpen(false);
  };

  const togglePool = (poolId) => {
    const next = new Set(expandedPools);
    if (next.has(poolId)) {
      next.delete(poolId);
    } else {
      next.add(poolId);
    }
    setExpandedPools(next);
  };

  const computePoolContracts = (pool) => {
    const result = [];
    contracts.forEach((contract) => {
      if (contract.voidedOn != null) { return; }
      const allocation = (contract.Allocations || contract.allocations || []).find(
        (a) => String(a.inventoryPoolId) === String(pool.id)
      );
      if (!allocation) { return; }

      const soldTons = contract.tonsSoldByPool
        ? (contract.tonsSoldByPool[pool.id] || 0)
        : 0;
      const remaining = allocation.tonsAllocated - soldTons;

      const supplier = contract.Supplier ||
        suppliers.find((s) => s.id === contract.supplierId);
      const project = contract.Project ||
        projects.find((p) => p.id === contract.projectId);

      result.push({
        contractId: String(contract.id),
        projectId: String(contract.projectId),
        projectName: project?.projectName || project?.name || "",
        supplierId: String(contract.supplierId),
        supplierName: supplier?.supplier || supplier?.name || "",
        totalTons: allocation.tonsAllocated,
        soldTons: Math.round(soldTons * 1000) / 1000,
        remainingTons: Math.round(remaining * 1000) / 1000,
        drawdownOrder: contract.projectDrawDownOrder || 1,
        contractPrice: contract.pricePerTon || 0,
        contractDate: contract.createdAt
          ? new Date(contract.createdAt).toLocaleDateString()
          : null
      });
    });
    return result;
  };

  const groupByProject = (poolContracts) => {
    const map = new Map();
    poolContracts.forEach((c) => {
      const existing = map.get(c.projectId) || [];
      map.set(c.projectId, [...existing, c]);
    });
    return map;
  };

  const getEffectiveOrder = (poolId, contractId, fallback) => {
    const poolLocal = localOrders[poolId];
    if (poolLocal) {
      const found = poolLocal.find((co) => co.contractId === contractId);
      if (found !== undefined) { return found.order; }
    }
    return fallback;
  };

  const handleMove = (pool, poolContracts, projectId, contractId, direction) => {
    const poolId = String(pool.id);
    const base = poolContracts.map((c) => ({
      contractId: c.contractId,
      order: getEffectiveOrder(poolId, c.contractId, c.drawdownOrder)
    }));

    const projectBase = base
      .filter((co) => {
        const pc = poolContracts.find((c) => c.contractId === co.contractId);
        return pc && pc.projectId === projectId;
      })
      .sort((a, b) => a.order - b.order);

    const idx = projectBase.findIndex((co) => co.contractId === contractId);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= projectBase.length) { return; }

    const updated = base.map((co) => ({ ...co }));
    const cur = updated.find((co) => co.contractId === contractId);
    const swap = updated.find((co) => co.contractId === projectBase[swapIdx].contractId);
    const tmp = cur.order;
    cur.order = swap.order;
    swap.order = tmp;

    setLocalOrders((prev) => ({ ...prev, [poolId]: updated }));
    onUpdateDrawdownOrder(poolId, updated);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">Inventory Pools</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsCreateOpen(true)}>
          Add Inventory Pool
        </Button>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {inventoryPools.map((pool) => {
          const poolContracts = computePoolContracts(pool);
          const poolId = String(pool.id);
          const totals = poolContracts.reduce(
            (acc, c) => ({
              total: acc.total + c.totalTons,
              sold: acc.sold + c.soldTons,
              remaining: acc.remaining + c.remainingTons
            }),
            { total: 0, sold: 0, remaining: 0 }
          );
          const grouped = groupByProject(poolContracts);
          const expanded = expandedPools.has(pool.id);

          return (
            <Card key={pool.id}>
              <CardHeader
                title={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                      size="small"
                      onClick={() => togglePool(pool.id)}
                      sx={{ minWidth: 32, p: 0.5 }}
                    >
                      {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Button>
                    <Typography variant="h4">{pool.inventoryPool}</Typography>
                  </Box>
                }
                subheader={
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">Available</Typography>
                    <Typography variant="h4" fontWeight="bold" color="success.main">
                      {totals.remaining.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}
                    </Typography>
                  </Box>
                }
              />
              {expanded && (
                <CardContent>
                  {Array.from(grouped.entries()).map(([projectId, pContracts]) => {
                    const projTotals = pContracts.reduce(
                      (acc, c) => ({
                        total: acc.total + c.totalTons,
                        sold: acc.sold + c.soldTons,
                        remaining: acc.remaining + c.remainingTons
                      }),
                      { total: 0, sold: 0, remaining: 0 }
                    );
                    const hasMultiple = pContracts.length > 1;
                    const sorted = [...pContracts]
                      .map((c) => ({
                        ...c,
                        effectiveOrder: getEffectiveOrder(poolId, c.contractId, c.drawdownOrder)
                      }))
                      .sort((a, b) => a.effectiveOrder - b.effectiveOrder);

                    return (
                      <Box key={projectId} sx={{ mb: 4 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, pb: 1, borderBottom: 1, borderColor: "divider" }}>
                          <Typography fontWeight="bold">
                            {pContracts[0].projectName}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 3 }}>
                            <Typography variant="body2" color="success.main" fontWeight="medium">
                              {projTotals.remaining.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })} tons available
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ overflowX: "auto" }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Priority</TableCell>
                                <TableCell>ContractID</TableCell>
                                <TableCell>Supplier</TableCell>
                                <TableCell>Price/Ton</TableCell>
                                <TableCell>Total Tons</TableCell>
                                <TableCell>Sold Tons</TableCell>
                                <TableCell>Available Tons</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(() => {
                                const active = sorted.filter((c) => !(c.totalTons > 0 && c.remainingTons <= 0));
                                const soldOut = sorted.filter((c) => c.totalTons > 0 && c.remainingTons <= 0);
                                return [...active, ...soldOut].map((contract) => {
                                  const isSoldOut = (contract.totalTons > 0 && contract.remainingTons <= 0) ||
                                    contract.fullyRetiredOn != null;
                                  const activeIndex = active.findIndex(
                                    (c) => c.contractId === contract.contractId
                                  );
                                  const showArrows = hasMultiple && !isSoldOut;
                                  return (
                                    <TableRow
                                      key={contract.contractId}
                                      sx={isSoldOut ? { opacity: 0.45, bgcolor: "grey.50" } : {}}
                                    >
                                      <TableCell>
                                        {showArrows
                                          ? (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                              <Box sx={{ display: "flex", flexDirection: "column" }}>
                                                <Button
                                                  size="small"
                                                  sx={{ minWidth: 24, p: 0.25 }}
                                                  onClick={() => handleMove(pool, poolContracts, projectId, contract.contractId, "up")}
                                                  disabled={activeIndex === 0}
                                                >
                                                  <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                                                </Button>
                                                <Button
                                                  size="small"
                                                  sx={{ minWidth: 24, p: 0.25 }}
                                                  onClick={() => handleMove(pool, poolContracts, projectId, contract.contractId, "down")}
                                                  disabled={activeIndex === active.length - 1}
                                                >
                                                  <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                                                </Button>
                                              </Box>
                                              <Typography fontWeight="medium">{activeIndex + 1}</Typography>
                                            </Box>
                                          )
                                          : (
                                            <Typography
                                              variant="body2"
                                              color={isSoldOut ? "text.disabled" : "text.primary"}
                                              sx={isSoldOut ? { fontStyle: "italic" } : {}}
                                            >
                                              {isSoldOut ? "Sold out" : contract.effectiveOrder}
                                            </Typography>
                                          )
                                        }
                                      </TableCell>
                                      <TableCell sx={{ fontFamily: "monospace" }}>
                                        {onNavigateToTab
                                          ? (
                                            <Box
                                              component="button"
                                              onClick={() => onNavigateToTab(0)}
                                              sx={{ color: isSoldOut ? "text.disabled" : "primary.main", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
                                            >
                                              {contract.contractId.substring(0, 8)}
                                            </Box>
                                          )
                                          : (
                                            contract.contractId.substring(0, 8)
                                          )}
                                      </TableCell>
                                      <TableCell>{contract.supplierName}</TableCell>
                                      <TableCell>${Number(contract.contractPrice).toFixed(2)}</TableCell>
                                      <TableCell>{Number(contract.totalTons).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</TableCell>
                                      <TableCell>{Number(contract.soldTons).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</TableCell>
                                      <TableCell>{Number(contract.remainingTons).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</TableCell>
                                    </TableRow>
                                  );
                                });
                              })()}
                            </TableBody>
                          </Table>
                        </Box>
                      </Box>
                    );
                  })}
                </CardContent>
              )}
            </Card>
          );
        })}
      </Box>

      <Dialog open={isCreateOpen} onClose={() => { setIsCreateOpen(false); setNewPoolName(""); }} maxWidth="xs" fullWidth>
        <DialogTitle>Create New Inventory Pool</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ pt: 1 }}>
            <TextField
              label="Pool Name"
              size="small"
              fullWidth
              value={newPoolName}
              onChange={(e) => setNewPoolName(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setIsCreateOpen(false); setNewPoolName(""); }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreatePool} disabled={!newPoolName}>
            Create Pool
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
